import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { styled, Theme } from '@mui/material/styles'
import { useDropzone, FileWithPath, FileRejection } from 'react-dropzone'
import Dialog from '../Dialog'
import Loading from '../Loading'
import { useUpload } from './useUpload'
import DeleteIcon from '@mui/icons-material/ClearRounded'
import UploadIcon from '@mui/icons-material/AddPhotoAlternateRounded'
// import FileIcon from '@mui/icons-material/NoteAddRounded'
// import { ReactComponent as DeleteIcon } from '../../icon/backoffice_delete.svg'
// import { ReactComponent as UploadIcon } from '../../icon/control_upload.svg'

const docImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/icon_doc.png`
const docxImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/icon_docx.png`
const pdfImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/icon_pdf.png`
const xlsxImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/icon_xlsx.png`

type UploadProps = {
    onChange?: (acceptedFiles: Partial<{ preview: string; name: string }[]>) => void
    data?: string[] | []
    value?: any
    label?: string
    placeholder?: string
    allowExtension?: string[]
    displayFileList?: boolean
    displayLabelFileList?: boolean
    labelFileList?: string
    disabled?: boolean
    fileSize?: number
    maxFile?: number // MB
    maxSize?: number // 1 MB = 1024 * 1024
    mock?: boolean
    icon?: React.ReactNode
}

const getNamePath = (path: string) => {
    if (typeof path === 'string') {
        return path.split('/').pop()
    } else return ''
}

const getExtensionPath = (path: string) => {
    if (typeof path === 'string') {
        let extension = path.split('.').pop()
        let type = ''
        if (extension === 'jpg' || extension === 'png' || extension === 'jpeg') {
            type = `image/${extension}`
        } else if (extension === 'pdf') {
            type = 'application/pdf'
        } else if (extension === 'sheet') {
            type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        } else {
            type = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }

        return type
    } else return ''
}

const getImageIcon = (file: any) => {
    if (file?.uploadDatetime) {
        let extension = file.urlPath.split('.').pop()
        if (extension === 'jpg' || extension === 'png' || extension === 'jpeg') {
            return file.urlPath
        } else if (file.type === 'application/pdf') {
            return pdfImg
        } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            return xlsxImg
        } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            return docxImg
        } else {
            return ''
        }
    } else if (file) {
        if (file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/jpeg') {
            return URL.createObjectURL(file)
        } else if (file.type === 'application/pdf') {
            return pdfImg
        } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            return xlsxImg
        } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            return docxImg
        } else {
            return ''
        }
    }
}

const UploadControl = (props: UploadProps) => {
    const {
        onChange,
        data,
        value,
        labelFileList = 'Files:',
        displayFileList = true,
        displayLabelFileList = true,
        disabled = false,
        fileSize = 5, // MB
        maxFile = 5,
        maxSize = fileSize * 1024 * 1024, // 1 MB = 1024 * 1024
        label = 'ลากวางหรือ อัพโหลดไฟล์',
        placeholder = 'ขนาดไฟล์ไม่เกิน 5MB  รองรับไฟล์ .jpg, .jpeg, .png',
        allowExtension = ['image/jpg', 'image/jpeg', 'image/png', '.pdf', '.doc', '.docx', '.xlsx'],
        mock = false,
        icon,
        // type = 'image',
    } = props

    const [files, setFiles] = useState<any[]>([])
    const [overMaxFiles, setOverMaxFiles] = useState(false)
    const [openDialog, setOpenDialog] = useState(false)
    const [checkData, setCheckData] = useState(false)
    const { upload, loading } = useUpload()

    const { t } = useTranslation()

    useEffect(() => {
        if (files.length >= maxFile) {
            setOverMaxFiles(true)
        } else {
            setOverMaxFiles(false)
        }
    }, [files])

    useEffect(() => {
        const conditionData = data && data.length > 0
        const conditionCheck = !checkData && conditionData

        if (conditionCheck && onChange) {
            // const acceptFilesInitial = acceptFilesData(data)
            // setFiles(setFilesData(data))
            // onChange(acceptFilesInitial)
            if (data && data.length > 0) {
                const acceptFilesInitial = setInitialFiles(data)
                setFiles(setInitialFiles(data))
                onChange?.(acceptFilesInitial)
                setCheckData(true)
            }
        }
    }, [data, checkData])

    useEffect(() => {
        if (value && value.length > 0) {
            const acceptFilesInitial = setInitialFiles(value)
            setFiles(setInitialFiles(value))
            onChange?.(acceptFilesInitial)
        }
    }, [])

    const validator = (file: FileWithPath) => {
        if (file.size > maxSize) {
            setOpenDialog(true)
            return {
                code: 'size-too-large',
                message: `File size is larger than ${fileSize} MB`,
            }
        }
        return null
    }

    const setInitialFiles = (data: any) => {
        return data?.map((item: any, index: number) => {
            if (item) {
                return {
                    ...item,
                    preview: item?.urlPath,
                    // name: item?.displayText,
                    name: item?.displayText ? item?.displayText : getNamePath(item?.urlPath),
                    type: getExtensionPath(item?.urlPath),
                    size: 1,
                    images: item,
                }
            } else {
                return {
                    ...item,
                    type: item?.type,
                    name: item?.name,
                }
            }
        })
    }

    // const setFilesData = (data: any) => {
    //     return data.map((item: any, index: number) => {
    //         if (typeof item !== 'object') {
    //             return {
    //                 preview: `${process.env.REACT_APP_MAIN}${item}`,
    //                 name: getNamePath(item),
    //                 type: getExtensionPath(item),
    //                 size: 1,
    //                 images: item,
    //             }
    //         } else {
    //             return {
    //                 ...item,
    //                 type: item.type,
    //                 name: item.name,
    //             }
    //         }
    //     })
    // }

    // const acceptFilesData = (data: any) => {
    //     return data.map((item: any) => {
    //         if (typeof item !== 'object') {
    //             return {
    //                 preview: `${process.env.REACT_APP_MAIN}${item}`,
    //                 name: getNamePath(item),
    //                 type: getExtensionPath(item),
    //                 size: 1,
    //                 images: item,
    //             }
    //         } else {
    //             return {
    //                 ...item,
    //             }
    //         }
    //     })
    // }

    const onDrop = async (acceptedFiles: any[], rejectedFiles?: FileRejection[] | undefined) => {
        setCheckData(true)
        setFiles(files.concat(acceptedFiles))

        // let uploadFile = []
        // if (!mock) {
        //     uploadFile = await Promise.all(
        //         acceptedFiles.map(async (file: any, index: number) => {
        //             const formData = new FormData()
        //             formData.append('files', file)
        //             formData.append('name', file.name)

        //             const { result, error } = await upload(formData)

        //             if (result) {
        //                 const resultFile = {
        //                     images: result?.data.binary,
        //                     name: file.name,
        //                     type: getExtensionPath(result.data.path),
        //                     preview: `${process.env.REACT_APP_UPLOAD_API}/${result.data.path}`,
        //                     error: '',
        //                 }

        //                 return resultFile
        //             } else {
        //                 return {
        //                     images: '',
        //                     preview: '',
        //                     error: error,
        //                 }
        //             }
        //         }),
        //     )

        if (onChange) {
            // const pack = files.concat(uploadFile)
            const pack = files.concat(acceptedFiles)
            onChange(pack)
            setFiles(pack)
        }
        // }
    }

    const onDelete = (indexs: number, event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()
        let newFile = files
        let filterNewFile = newFile.filter(function (item, index) {
            return indexs !== index
        })
        setFiles(filterNewFile)
        if (onChange) {
            onChange(filterNewFile)
        }
    }

    const PreviewComponent = files.map((file: any, index: number) => {
        return (
            <Preview
                key={`filename-${index}`}
                onClick={() => {
                    globalThis.open(file.urlPath ? file.urlPath : URL.createObjectURL(file))
                }}
                style={{ cursor: 'pointer' }}
            >
                <PreviewControlImage>
                    <PreviewImg src={getImageIcon(file)} />
                </PreviewControlImage>
                <PreviewControl>
                    <PreviewControlFile>
                        <PreviewNameFile>{file.name}</PreviewNameFile>
                    </PreviewControlFile>
                </PreviewControl>
                {!disabled && (
                    <PreviewIconRemove onClick={(event: React.MouseEvent<HTMLDivElement>) => onDelete(index, event)}>
                        <IconDelete />
                    </PreviewIconRemove>
                )}
            </Preview>
        )
    })

    const { getRootProps, getInputProps } = useDropzone({
        noClick: overMaxFiles,
        noDrag: overMaxFiles,
        multiple: true,
        accept: allowExtension,
        onDrop: (acceptedFiles, rejectedFiles) => onDrop(acceptedFiles, rejectedFiles),
        validator: validator,
        disabled: disabled || loading,
        maxFiles: maxFile,
        maxSize: maxSize,
    })
    const classes = useStyles()

    return (
        <Container className="container">
            {!disabled && files.length < maxFile && (
                <Dropzone {...getRootProps({ className: 'dropzone' })} loading={loading.toString()}>
                    <input {...getInputProps()} accept={allowExtension.toString()} />
                    {icon ?? <UploadIcon className={classes.icon} />}
                    {/* <IconUpload loading={loading.toString()} /> */}
                    <DivLabel>{label}</DivLabel>
                    <DivPlaceholder>{placeholder}</DivPlaceholder>
                </Dropzone>
            )}
            {files && files.length > 0 && (
                <>
                    {!disabled && displayLabelFileList && <h3 style={{ color: '#797D7F' }}>{labelFileList}</h3>}
                    {displayFileList && PreviewComponent}
                </>
            )}
            {loading && (
                <DivLoading>
                    <Loading size={150} />
                </DivLoading>
            )}
            <Dialog
                title={t('ALERT.TITLE.ERROR')}
                content={
                    maxFile === 1
                        ? t('ALERT.CONTENT.UPLOAD_NOT_EXCEED_1_FILE').replace('{fileSize}', fileSize.toString())
                        : t('ALERT.CONTENT.UPLOAD_NOT_EXCEED_MAX_FILE')
                              .replace('{fileSize}', fileSize.toString())
                              .replace('{maxFile}', maxFile.toString())
                }
                isOpen={openDialog}
                onClose={() => setOpenDialog(false)}
            />
        </Container>
    )
}

export default UploadControl

const useStyles = makeStyles((theme: Theme) => ({
    icon: {
        fontSize: '70px !important',
        color: '#318AF14D',
        opacity: 30,
    },
}))

const Container = styled('div')(({ theme }) => ({
    width: '100%',
}))

const DivLoading = styled('div')(({ theme }) => ({
    position: 'relative',
    top: '-4px',
    height: '70px',
    padding: '10px 0px',
}))

const DivLabel = styled('div')(({ theme }) => ({
    color: '#000',
    fontWeight: 'bold',
}))

const DivPlaceholder = styled('div')(({ theme }) => ({
    color: '#C9C9C9',
}))

const Dropzone = styled('div')(({ theme, loading }: any) => ({
    display: 'flex',
    objectFit: 'cover',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: '1px',
    borderRadius: '10px',
    borderStyle: 'dashed',
    backgroundColor: theme.palette.text.secondary,
    color: '#bdbdbd',
    outline: 'none',
    cursor: 'pointer',
    borderColor: loading === 'true' ? theme.palette.text.disabled : '#C4C4C4',
}))

const Preview = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '8px 0',
}))

const PreviewControlImage = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '150px',
    height: '100%',
}))

const PreviewImg = styled('img')(({ theme, src }) => ({
    maxWidth: '50px',
    // height: '50px',
}))

const PreviewControl = styled('div')(({ theme }) => ({
    flexGrow: 1,
    width: '100%',
}))

const PreviewControlFile = styled('div')(({ theme }) => ({
    height: '100%',
}))

const PreviewNameFile = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
}))

const PreviewIconRemove = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100px',
    height: '100%',
    cursor: 'pointer',
}))

// const IconUpload = styled(UploadIcon)(({ theme, loading }: any) => ({
//     width: '80px',
//     color: loading === 'true' ? theme.palette.text.disabled : theme.palette.primary.main,
// }))

// const IconDelete = styled(DeleteIcon)(({ theme }) => ({
//     width: '25px',
//     height: '25px',
//     color: '#c40000',
// }))

// const IconDelete = styled(DeleteIcon)(({ theme }) => ({
//     height: '18px',
//     fill: theme.palette.getContrastText('required'),
// }))

const IconDelete = styled(DeleteIcon)(({ theme }) => ({
    fontSize: '18px',
    color: theme.palette.text.disabled,
}))
